import get from 'lodash/get'
import * as amplitude from '@amplitude/analytics-browser'
import { plugin as engagementPlugin } from '@amplitude/engagement-browser'

import { isPsmBasedApp, isShelflifeCoreApp } from '../../van-shared/utils/utils'
import { getUserTrackingHandler } from './events/user'
import { getShipmentTrackingHandler } from './events/shipments'
import { getReportTrackingHandler } from './events/reports'
import { getAnalyticsTrackingHandler } from './events/analytics'
import { getFacilityProductsHandler } from './events/facility-products'
import { getAppPerformanceTrackingHandler } from './events/app-performance'

const USER_ROLE_PREFIX = 'feature:userRole:'
const TRACKING_KEY = '__analytics:amplitudeInitialized'
const ENGAGEMENT_PREVIEW_PARAM = 'ampPreview'

export const ANALYTICS_EVENTS = {
  APP_POST_LOGIN_READY: 'app_post_login_ready',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  MODULE_VISITED: 'module_visited',
  CREDIT_APPLICATION_VISITED: 'credit_application_page_visited',
  CREDIT_APPLICATION_SENT: 'credit_application_sent',
  CREDIT_CONTRACT_VISITED: 'credit_contract_page_visited',
  CREDIT_CONTRACT_SIGNED: 'credit_contract_signed',
  SHIPMENT_VIEWED: 'shipment_viewed',
  SHIPMENT_DELIVERY_STARTED: 'shipment_delivery_started',
  SHIPMENT_DELIVERY_CONFIRMED: 'shipment_delivery_confirmed',
  SHIPMENT_DISCREPANCY_FLAGGED: 'shipment_discrepancy_flagged',
  STOCK_REPORT_STARTED: 'stock_report_started',
  STOCK_REPORT_SAVED: 'stock_report_saved',
  STOCK_REPORT_SUBMITTED: 'stock_report_submitted',
  STOCK_REPORT_EDITED: 'stock_report_edited',
  STOCK_FIELD_FILLED: 'stock_field_filled',
  BATCH_EXPIRY_REPORTED: 'batch_expiry_reported',
  ANALYTICS_MODULE_VISITED: 'analytics_module_visited',
  PRODUCT_VIEWED: 'product_viewed',
  PRODUCT_ADDED_TO_CART: 'product_added_to_cart',
  CHECKOUT_STARTED: 'checkout_started',
  ORDER_COMPLETED: 'order_completed',
  ORDER_PAYMENT_FAILED: 'order_payment_failed',
  ORDERING_SESSION_STARTED: 'order_session_started',
  ORDERING_SESSION_RESUMED: 'order_session_resumed',
  ORDER_DRAFT_SAVED: 'order_draft_saved',
  ORDER_REVIEW_VISITED: 'order_review_visited',
  ORDER_UNAVAILABLE_ITEMS_REMOVED: 'order_products_autoremoved',
  ORDER_SUBMISSION_FAILED: 'order_submission_failed',
  PRODUCTS_SEARCHED: 'product_search_performed',
  ORDER_BUDGET_SET: 'order_budget-set'
}

// Currently allows remote event logging for all apps.
// Kept as a management layer in case logging needs to be restricted per tenant.
export const canLogEventsRemotely = (config) => {
  return isShelflifeCoreApp(config) || isPsmBasedApp(config)
}

/**
 * Logs events to Amplitude (remote).
 */
const logEventRemotely = (eventName, data = {}) => {
  return amplitude.track(eventName, data).promise
}

/**
 * Logs events locally to the console (used when tracking is disabled).
 * We can remove the log if it's no longer needed
 */
const logEventLocally = (eventName, data = {}) => {
  console.log('[Amplitude Local]', eventName, data)
  return data
}

/**
 * Load Guides & Surveys plugin
 */
export const loadEngagementPlugin = (user) => {
  amplitude.add(engagementPlugin())
}

/**
 * Init Amplitude
*/
export const initAmplitude = async (config) => {
  const apiKey = get(config, 'amplitude.apiKey', '')

  if (!apiKey || !canLogEventsRemotely(config)) {
    // we are resetting the localStorage session flag in case user had it set to true previously (it used to work differently)
    updateAmplitudeSessionFlag(true)
    return
  }

  // Testers previewing a guide that should render before/without login
  // append ?ampPreview=1 to the URL they hand to Amplitude's Preview flow.
  // This keeps the ~10 extra network requests the engagement plugin makes
  // off the normal login-page path for everyone else.
  if (isEngagementPreviewRequested()) {
    await amplitude.init(apiKey, { autocapture: true }).promise
    loadEngagementPlugin()
  } else {
    // Initialize Amplitude with the API key.
    // Autocapture is enabled to automatically track page views and navigation events.
    amplitude.init(apiKey, { autocapture: true })
  }

  updateAmplitudeSessionFlag()
}

const logUserSessionEvent = async (eventName, userId, logHandler) => {
  if (!userId) return

  return logHandler(eventName, {
    user_id: userId,
    timestamp: new Date().toISOString()
  })
}

export const trackLoginAmplitude = (config, user) => {
  if (!(isAmplitudeSessionActive() || !user)) return
  const { userId, userName, userRole, userRoles } = getUserDetails(config, user)

  // Set the Amplitude user ID
  if (userId) {
    amplitude.setUserId(userId)
  }

  // Create a new Amplitude Identify object and assign user traits
  const identifyObject = new amplitude.Identify()
    .set('name', userName)
    .set('role', userRole)
    .set('roles', userRoles)
    .set('organization', config.app)

  // Send the user properties to Amplitude
  amplitude.identify(identifyObject)

  // Set the user's group (for things like org-level tracking or comparisons)
  amplitude.setGroup('orgId', config.app)

  logUserSessionEvent(ANALYTICS_EVENTS.USER_LOGGED_IN, userId, logEventRemotely)
}

/**
 * Logout in Amplitude
 */
export const trackLogoutAmplitude = async (config, user) => {
  if (!(isAmplitudeSessionActive())) return

  const { userId } = getUserDetails(config, user)
  if (canLogEventsRemotely(config)) {
    await logUserSessionEvent(ANALYTICS_EVENTS.USER_LOGGED_OUT, userId, logEventRemotely)
  }
  amplitude.reset()
  // reset the localStorage flag
  updateAmplitudeSessionFlag(true)
}

export const setLocationInAmplitude = (location) => {
  if (!(isAmplitudeSessionActive() || !location)) return

  // Create a new Amplitude Identify object and assign location
  const identifyObject = new amplitude.Identify()
    .set('location_id', location._id)
    .set('pharmacy_name', (location.storeFullName || location.storeName))

  // Send the user properties to Amplitude
  amplitude.identify(identifyObject)
}

/**
 * Returns the appropriate event logger depending on whether remote tracking is allowed.
 *
 * This wrapper is especially useful for class components, which can't use React hooks
 * (e.g., useAmplitudeAnalytics), allowing them to conditionally log events based on tenant settings.
 */
export const getAnalyticsEventTrackingHandler = (config, user) => {
  const isRemote = canLogEventsRemotely(config)
  const events = {
    track: isRemote ? logEventRemotely : logEventLocally
  }
  const trackingContext = { config, user, events }

  return {
    user: getUserTrackingHandler(trackingContext),
    shipment: getShipmentTrackingHandler(trackingContext),
    report: getReportTrackingHandler(trackingContext),
    analytics: getAnalyticsTrackingHandler(trackingContext),
    facilityProducts: getFacilityProductsHandler(trackingContext),
    appPerformance: getAppPerformanceTrackingHandler(trackingContext)
  }
}

const extractUserRole = (roles = [], app = '') => {
  const raw = roles.find(role => role.startsWith(USER_ROLE_PREFIX))
  if (!raw) return []

  // Remove any existing prefix (e.g., "psm-", "shelflife-")
  const baseRole = raw.replace(USER_ROLE_PREFIX, '').replace(/^(psm-|shelflife-)/, '')

  // Add the correct prefix based on config.app
  return `${app}-${baseRole}`
}

export const getUserDetails = (config, user) => {
  const userId = user._id.split('org.couchdb.user:')[1]
  const fullName = get(user, 'profile.fullName') || get(user, 'fullName')
  const userName = fullName || user.name.replaceAll('-', ' ')
  const userRoles = user.roles
  const userRole = extractUserRole(userRoles, config.app)

  return {
    userId,
    userName,
    userRoles,
    userRole
  }
}

export const isAmplitudeSessionActive = () => {
  return !!window.localStorage.getItem(TRACKING_KEY)
}

export const updateAmplitudeSessionFlag = (resetSession = false) => {
  if (resetSession) {
    window.localStorage.removeItem(TRACKING_KEY)
    return
  }

  const isTracking = isAmplitudeSessionActive()
  if (isTracking) return

  window.localStorage.setItem(TRACKING_KEY, 'true')
}

export const isEngagementPreviewRequested = () => {
  try {
    return new URLSearchParams(window.location.search).has(ENGAGEMENT_PREVIEW_PARAM)
  } catch (error) {
    return false
  }
}
